import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  TableOutlined,
  TeamOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Modal, Spin, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table/Table';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import { DonorStatus } from 'gql/types';
import {
  formatCsvDate,
  formatCsvDateOnly,
} from 'modules/dashboard/utils/csvDate';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import { getDateRangeBounds } from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import {
  GET_OPS_OVERVIEW_STATS_QUERY,
  GET_RECRUITER_LOCATION_STATS_QUERY,
} from '../../graphql/queries';
import { GET_OPS_LOCATION_UNITS } from '../../graphql/queries/data-audit';
import { usePrbcDonationDonors } from '../../hooks/usePrbcDonationDonors';
import { IDonorRecord } from '../../types/donor';
import { DateRangeType } from '../../types/phlebotomist-dashboard';
import {
  buildOpsDrilldownUrl,
  OPS_DRILLDOWN_META,
} from '../../utils/buildOpsDrilldownUrl';
import { getDateRangeDisplayLabel } from '../../utils/dateRangeLabels';
import { DonorDonationHistoryModal } from '../DonorDetailsTabContent/components/DonorDonationHistoryModal';
import { makeFilterDropdown } from '../DonorDetailsTabContent/components/FilterDropdown';
import styles from '../OperationsDashboard/ops-theme.module.css';

/* ── Valid date range set ── */

const VALID_DATE_RANGES: ReadonlySet<string> = new Set<DateRangeType>([
  'day',
  'yesterday',
  'week',
  'lastWeek',
  'month',
  'lastMonth',
  'quarter',
  'lastQuarter',
  'year',
  'lastYear',
  'dayCal',
  'yesterdayCal',
  'weekCal',
  'lastWeekCal',
  'monthCal',
  'lastMonthCal',
  'quarterCal',
  'lastQuarterCal',
  'yearCal',
  'pYearCal',
  'custom',
]);

/* ── Table type → donor status filter mapping ── */

const ALL_DONOR_STATUSES = Object.values(DonorStatus);

const STATUS_FILTER_MAP: Record<string, DonorStatus[]> = {
  allLocationDonors: ALL_DONOR_STATUSES,
  recruited: ALL_DONOR_STATUSES,
  'summary:recruited': ALL_DONOR_STATUSES,
  newRecruits: ALL_DONOR_STATUSES,
  newRegistration: ALL_DONOR_STATUSES,
  activeDonors: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  pending: [DonorStatus.Pending],
  pipelineStall: [DonorStatus.Pending],
  retired: [DonorStatus.Retired],
  retiringIn30: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  retiringIn60: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  retiringIn90: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  appliedToActive: ALL_DONOR_STATUSES,
  pendingToApproved: ALL_DONOR_STATUSES,
  activeToRetired: [
    DonorStatus.Retired,
    DonorStatus.Archived,
    DonorStatus.NotAccepted,
  ],
  pendingToArchived: [DonorStatus.Archived], // CTE also filters to current status = Archived
};

/* ── Period-scoped tables (use dateRange for query) ── */

const PERIOD_TABLES = new Set(['unitMom', 'retired']);

/* ── Registration-scoped tables (filter by createdAt within dateRange) ── */

const REGISTRATION_TABLES = new Set([
  'recruited',
  'summary:recruited',
  'newRecruits',
  'newRegistration',
]);

/* ── Tables whose default sort is Date Registered DESC ── */

const SORT_BY_REGISTERED_DESC = new Set([
  'recruited',
  'summary:recruited',
  'newRecruits',
  'newRegistration',
  'activeDonors',
  'allLocationDonors',
]);

/* ── Status colors ── */

const STATUS_COLORS: Record<string, string> = {
  [DonorStatus.Active]: '#52c41a',
  [DonorStatus.Approved]: '#1565c0',
  [DonorStatus.Pending]: '#faad14',
  [DonorStatus.Registered]: '#722ed1',
  [DonorStatus.Retired]: '#8c8c8c',
  [DonorStatus.Archived]: '#8c8c8c',
  [DonorStatus.AnnualLabworkDue]: '#1890ff',
};

const TYPE_COLORS = { DBL: '#f31a25', SGL: '#1565c0' };

/* ── Column builder ── */

function buildColumns(
  donors: IDonorRecord[],
  periodLabel?: string,
  dataTableId?: string,
  isDarkMode?: boolean
): ColumnsType<IDonorRecord> {
  const isRetiring = dataTableId?.startsWith('retiringIn') ?? false;
  const isRetired = dataTableId === 'retired';
  const isActiveToRetired = dataTableId === 'activeToRetired';
  const isPendingToApproved = dataTableId === 'pendingToApproved';
  const isPendingToArchived = dataTableId === 'pendingToArchived';
  const isAppliedToActive =
    dataTableId === 'appliedToActive' || isActiveToRetired;
  const sortByRegistered =
    isAppliedToActive ||
    (dataTableId ? SORT_BY_REGISTERED_DESC.has(dataTableId) : false);
  const unique = (fn: (d: IDonorRecord) => string | null | undefined) =>
    [...new Set(donors.map(fn).filter(Boolean))].sort() as string[];

  const uniqueOwners = unique((d) => d.ownerFullName);
  const uniqueDonors = unique((d) => d.donorName);
  const uniqueBreeds = unique((d) => d.breed);
  const uniqueStatuses = unique((d) => d.status);
  const uniqueLocations = unique((d) => d.ownerAssignedLocations || d.location);
  const uniqueRegions = unique((d) => d.region);

  const mkFilter = (vals: string[]) =>
    makeFilterDropdown(
      vals.map((v) => ({ text: v, value: v })),
      isDarkMode
    );

  return [
    {
      title: 'Date Registered',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      width: 120,
      render: (date: string | null) =>
        date ? format(new Date(date), 'MM/dd/yyyy') : '-',
      defaultSortOrder: sortByRegistered ? 'descend' : undefined,
      sorter: (a, b) => {
        const at = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
        const bt = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
        return at - bt;
      },
    },
    ...(isAppliedToActive
      ? [
          {
            title: 'Active Date',
            dataIndex: 'activeDate' as const,
            key: 'activeDate',
            width: 120,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.activeDate ? new Date(a.activeDate).getTime() : 0;
              const bt = b.activeDate ? new Date(b.activeDate).getTime() : 0;
              return at - bt;
            },
          },
          ...(isActiveToRetired
            ? [
                {
                  title: 'Retired Date',
                  dataIndex: 'retiredDate' as const,
                  key: 'retiredDate',
                  width: 120,
                  render: (date: string | null) =>
                    date ? format(new Date(date), 'MM/dd/yyyy') : '-',
                  sorter: (a: IDonorRecord, b: IDonorRecord) => {
                    const at = a.retiredDate
                      ? new Date(a.retiredDate).getTime()
                      : 0;
                    const bt = b.retiredDate
                      ? new Date(b.retiredDate).getTime()
                      : 0;
                    return at - bt;
                  },
                },
                {
                  title: 'Active Tenure',
                  key: 'activeTenure',
                  width: 120,
                  align: 'center' as const,
                  render: (_: unknown, record: IDonorRecord) => {
                    if (!record.activeDate || !record.retiredDate) return '-';
                    const days = Math.round(
                      (new Date(record.retiredDate).getTime() -
                        new Date(record.activeDate).getTime()) /
                        86400000
                    );
                    if (days < 0) return '-';
                    if (days >= 365) return `${(days / 365).toFixed(1)}y`;
                    return `${days}d`;
                  },
                  sorter: (a: IDonorRecord, b: IDonorRecord) => {
                    const calc = (r: IDonorRecord) =>
                      r.activeDate && r.retiredDate
                        ? new Date(r.retiredDate).getTime() -
                          new Date(r.activeDate).getTime()
                        : -1;
                    return calc(a) - calc(b);
                  },
                },
              ]
            : [
                {
                  title: 'To Active Days',
                  key: 'toActiveDays',
                  width: 120,
                  align: 'center' as const,
                  render: (_: unknown, record: IDonorRecord) => {
                    if (!record.registeredAt || !record.activeDate) return '-';
                    const days = Math.round(
                      (new Date(record.activeDate).getTime() -
                        new Date(record.registeredAt).getTime()) /
                        86400000
                    );
                    return days >= 0 ? `${days}d` : '-';
                  },
                  sorter: (a: IDonorRecord, b: IDonorRecord) => {
                    const calc = (r: IDonorRecord) =>
                      r.registeredAt && r.activeDate
                        ? new Date(r.activeDate).getTime() -
                          new Date(r.registeredAt).getTime()
                        : -1;
                    return calc(a) - calc(b);
                  },
                },
              ]),
        ]
      : []),
    ...(isPendingToApproved
      ? [
          {
            title: 'Approved Date',
            dataIndex: 'approvedDate' as const,
            key: 'approvedDate',
            width: 120,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.approvedDate
                ? new Date(a.approvedDate).getTime()
                : 0;
              const bt = b.approvedDate
                ? new Date(b.approvedDate).getTime()
                : 0;
              return at - bt;
            },
          },
          {
            title: 'To Approved Days',
            key: 'toApprovedDays',
            width: 130,
            align: 'center' as const,
            render: (_: unknown, record: IDonorRecord) => {
              if (!record.registeredAt || !record.approvedDate) return '-';
              const days = Math.round(
                (new Date(record.approvedDate).getTime() -
                  new Date(record.registeredAt).getTime()) /
                  86400000
              );
              return days >= 0 ? `${days}d` : '-';
            },
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const calc = (r: IDonorRecord) =>
                r.registeredAt && r.approvedDate
                  ? new Date(r.approvedDate).getTime() -
                    new Date(r.registeredAt).getTime()
                  : -1;
              return calc(a) - calc(b);
            },
            defaultSortOrder: 'descend' as const,
          },
        ]
      : []),
    ...(isPendingToArchived
      ? [
          {
            title: 'Archived Date',
            dataIndex: 'archivedDate' as const,
            key: 'archivedDate',
            width: 120,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.archivedDate
                ? new Date(a.archivedDate).getTime()
                : 0;
              const bt = b.archivedDate
                ? new Date(b.archivedDate).getTime()
                : 0;
              return at - bt;
            },
          },
          {
            title: 'To Archived Days',
            key: 'toArchivedDays',
            width: 130,
            align: 'center' as const,
            render: (_: unknown, record: IDonorRecord) => {
              if (!record.registeredAt || !record.archivedDate) return '-';
              const days = Math.round(
                (new Date(record.archivedDate).getTime() -
                  new Date(record.registeredAt).getTime()) /
                  86400000
              );
              return days >= 0 ? `${days}d` : '-';
            },
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const calc = (r: IDonorRecord) =>
                r.registeredAt && r.archivedDate
                  ? new Date(r.archivedDate).getTime() -
                    new Date(r.registeredAt).getTime()
                  : -1;
              return calc(a) - calc(b);
            },
            defaultSortOrder: 'descend' as const,
          },
        ]
      : []),
    {
      title: 'Owner',
      dataIndex: 'ownerFullName',
      key: 'ownerFullName',
      width: 150,
      render: (name: string | null, record) =>
        name && record.ownerShortId ? (
          <Link
            to={Routes.EditOwner.replace(':id', String(record.ownerShortId))}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1565c0' }}
          >
            {name}
          </Link>
        ) : (
          name || '-'
        ),
      sorter: (a, b) =>
        (a.ownerFullName || '').localeCompare(b.ownerFullName || ''),
      filters: uniqueOwners.map((n) => ({ text: n, value: n })),
      filterDropdown: mkFilter(uniqueOwners),
      onFilter: (value, record) => record.ownerFullName === value,
    },
    {
      title: 'Donor',
      dataIndex: 'donorName',
      key: 'donorName',
      width: 120,
      render: (name: string, record) => (
        <Link
          to={Routes.DonorsProfile.replace(':id', String(record.donorShortId))}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1565c0' }}
        >
          {name}
        </Link>
      ),
      sorter: (a, b) => a.donorName.localeCompare(b.donorName),
      filters: uniqueDonors.map((n) => ({ text: n, value: n })),
      filterDropdown: mkFilter(uniqueDonors),
      onFilter: (value, record) => record.donorName === value,
    },
    ...(isRetired
      ? []
      : [
          {
            title: 'Breed',
            dataIndex: 'breed' as const,
            key: 'breed',
            width: 120,
            render: (val: string | null) => val || '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              (a.breed || '').localeCompare(b.breed || ''),
            filters: uniqueBreeds.map((b) => ({ text: b, value: b })),
            filterDropdown: mkFilter(uniqueBreeds),
            onFilter: (value: unknown, record: IDonorRecord) =>
              record.breed === value,
          },
        ]),
    ...(isRetiring
      ? [
          {
            title: 'Birthday',
            dataIndex: 'dateOfBirth' as const,
            key: 'dateOfBirth',
            width: 110,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
              const bt = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
              return at - bt;
            },
          },
          {
            title: 'Retires On',
            key: 'retiresOn',
            width: 110,
            render: (_: unknown, record: IDonorRecord) => {
              if (!record.dateOfBirth) return '-';
              const dob = new Date(record.dateOfBirth);
              const retires = new Date(
                dob.getFullYear() + 9,
                dob.getMonth(),
                dob.getDate()
              );
              return format(retires, 'MM/dd/yyyy');
            },
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
              const bt = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
              return at - bt;
            },
            defaultSortOrder: 'ascend' as const,
          },
        ]
      : []),
    ...(isRetired
      ? [
          {
            title: 'Birthday',
            dataIndex: 'dateOfBirth' as const,
            key: 'dateOfBirth',
            width: 110,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
              const bt = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
              return at - bt;
            },
          },
          {
            title: 'Retired Date',
            dataIndex: 'retiredDate' as const,
            key: 'retiredDate',
            width: 120,
            defaultSortOrder: 'descend' as const,
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.retiredDate ? new Date(a.retiredDate).getTime() : 0;
              const bt = b.retiredDate ? new Date(b.retiredDate).getTime() : 0;
              return at - bt;
            },
          },
          {
            title: 'Status',
            dataIndex: 'status' as const,
            key: 'status',
            width: 100,
            render: (status: string) => (
              <span style={{ color: STATUS_COLORS[status] || '#ccc' }}>
                {status}
              </span>
            ),
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              a.status.localeCompare(b.status),
            filters: uniqueStatuses.map((s) => ({ text: s, value: s })),
            filterDropdown: mkFilter(uniqueStatuses),
            onFilter: (value: unknown, record: IDonorRecord) =>
              record.status === value,
          },
          {
            title: 'Retired Reason',
            dataIndex: 'retiredReason' as const,
            key: 'retiredReason',
            width: 130,
            render: (val: string | null) => val || '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              (a.retiredReason || '').localeCompare(b.retiredReason || ''),
          },
        ]
      : []),
    {
      title: 'Location',
      key: 'location',
      width: 140,
      render: (_: unknown, record: IDonorRecord) => {
        const raw = record.ownerAssignedLocations || record.location || '';
        if (!raw) return '-';
        const parts = raw.split(', ');
        if (parts.length <= 3) return raw;
        return `${parts.slice(0, 3).join(', ')} +${parts.length - 3} more`;
      },
      sorter: (a: IDonorRecord, b: IDonorRecord) =>
        (a.ownerAssignedLocations || a.location).localeCompare(
          b.ownerAssignedLocations || b.location
        ),
      filters: uniqueLocations.map((loc) => ({ text: loc, value: loc })),
      filterDropdown: mkFilter(uniqueLocations),
      onFilter: (value, record) =>
        (record.ownerAssignedLocations || record.location) === value,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      width: 120,
      render: (val: string | null) => val || '-',
      sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
      filters: uniqueRegions.map((r) => ({ text: r, value: r })),
      filterDropdown: mkFilter(uniqueRegions),
      onFilter: (value, record) => record.region === value,
    },
    ...(isAppliedToActive
      ? []
      : [
          {
            title: 'Last Donation',
            dataIndex: 'lastDonationDate' as const,
            key: 'lastDonationDate',
            width: 120,
            defaultSortOrder:
              isRetiring || isRetired || sortByRegistered
                ? undefined
                : ('descend' as const),
            render: (date: string | null) =>
              date ? format(new Date(date), 'MM/dd/yyyy') : '-',
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.lastDonationDate
                ? new Date(a.lastDonationDate).getTime()
                : 0;
              const bt = b.lastDonationDate
                ? new Date(b.lastDonationDate).getTime()
                : 0;
              return at - bt;
            },
          },
        ]),
    ...(isRetiring
      ? [
          {
            title: 'TTM Success Rate',
            key: 'ttmSuccessRate',
            width: 110,
            align: 'center' as const,
            render: (_: unknown, record: IDonorRecord) => {
              if (!record.registeredAt) return '-';
              const daysSinceReg = Math.max(
                Math.round(
                  (Date.now() - new Date(record.registeredAt).getTime()) /
                    86400000
                ),
                1
              );
              return ((record.periodUnits / daysSinceReg) * 365).toFixed(1);
            },
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const calc = (r: IDonorRecord) => {
                if (!r.registeredAt) return 0;
                const days = Math.max(
                  Math.round(
                    (Date.now() - new Date(r.registeredAt).getTime()) / 86400000
                  ),
                  1
                );
                return (r.periodUnits / days) * 365;
              };
              return calc(a) - calc(b);
            },
          },
          {
            title: 'Time in Prog',
            key: 'timeInProg',
            width: 110,
            align: 'center' as const,
            render: (_: unknown, record: IDonorRecord) => {
              if (!record.registeredAt) return '-';
              const start = new Date(record.registeredAt);
              const now = new Date();
              let years = now.getFullYear() - start.getFullYear();
              let months = now.getMonth() - start.getMonth();
              let days = now.getDate() - start.getDate();
              if (days < 0) {
                months -= 1;
                days += new Date(
                  now.getFullYear(),
                  now.getMonth(),
                  0
                ).getDate();
              }
              if (months < 0) {
                years -= 1;
                months += 12;
              }
              return `${years}y ${months}m ${days}d`;
            },
            sorter: (a: IDonorRecord, b: IDonorRecord) => {
              const at = a.registeredAt
                ? new Date(a.registeredAt).getTime()
                : 0;
              const bt = b.registeredAt
                ? new Date(b.registeredAt).getTime()
                : 0;
              return at - bt;
            },
          },
        ]
      : []),
    ...(!isRetired
      ? [
          {
            title: 'Status',
            dataIndex: 'status' as const,
            key: 'status',
            width: 100,
            render: (status: string) => (
              <span style={{ color: STATUS_COLORS[status] || '#ccc' }}>
                {status}
              </span>
            ),
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              a.status.localeCompare(b.status),
            filters: uniqueStatuses.map((s) => ({ text: s, value: s })),
            filterDropdown: mkFilter(uniqueStatuses),
            onFilter: (value: unknown, record: IDonorRecord) =>
              record.status === value,
          },
        ]
      : []),
    {
      title: 'SGL/DBL',
      dataIndex: 'sglDbl',
      key: 'sglDbl',
      width: 80,
      align: 'center',
      render: (val: string) => (
        <Tag color={val === 'DBL' ? TYPE_COLORS.DBL : TYPE_COLORS.SGL}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => a.sglDbl.localeCompare(b.sglDbl),
      filters: [
        { text: 'SGL', value: 'SGL' },
        { text: 'DBL', value: 'DBL' },
      ],
      filterDropdown: makeFilterDropdown(
        [
          { text: 'SGL', value: 'SGL' },
          { text: 'DBL', value: 'DBL' },
        ],
        isDarkMode
      ),
      onFilter: (value, record) => record.sglDbl === value,
    },
    ...(isRetired || isAppliedToActive || isRetiring
      ? []
      : [
          {
            title: periodLabel ? (
              <span>
                Units
                <br />({periodLabel})
              </span>
            ) : (
              'Units'
            ),
            dataIndex: 'periodUnits' as const,
            key: 'periodUnits',
            width: 100,
            align: 'center' as const,
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              a.periodUnits - b.periodUnits,
          },
        ]),
    ...(isRetiring
      ? []
      : [
          {
            title: 'Total Donations',
            dataIndex: 'totalDonations' as const,
            key: 'totalDonations',
            width: 90,
            align: 'center' as const,
            sorter: (a: IDonorRecord, b: IDonorRecord) =>
              a.totalDonations - b.totalDonations,
          },
        ]),
    ...(isAppliedToActive
      ? []
      : [
          {
            title: 'Blood Type',
            dataIndex: 'bloodTypeDisplay' as const,
            key: 'bloodTypeDisplay',
            width: 80,
            align: 'center',
            sorter: (a, b) =>
              a.bloodTypeDisplay.localeCompare(b.bloodTypeDisplay),
            filters: [
              { text: 'P (Positive)', value: 'P' },
              { text: 'N (Negative)', value: 'N' },
              { text: 'U (Unknown)', value: 'U' },
            ],
            filterDropdown: makeFilterDropdown(
              [
                { text: 'P (Positive)', value: 'P' },
                { text: 'N (Negative)', value: 'N' },
                { text: 'U (Unknown)', value: 'U' },
              ],
              isDarkMode
            ),
            onFilter: (value, record) => record.bloodTypeDisplay === value,
          },
        ]),
  ];
}

/* ── CSV export helper ── */

function csvEscape(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

/* ══════════════════════════════════════════
   Main Component
   ══════════════════════════════════════════ */

const OpsDrilldownTab: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setLastDrilldownUrl, opsSettings, isDarkMode } =
    useOperationsDashboardContext();

  const [selectedDonor, setSelectedDonor] = useState<IDonorRecord | null>(null);
  const [drilldownVisible, setDrilldownVisible] = useState(false);
  const filteredDonorsRef = useRef<IDonorRecord[] | null>(null);

  // Persist current URL for tab re-entry
  useEffect(() => {
    const url = location.pathname + location.search;
    if (location.search) setLastDrilldownUrl(url);
  }, [location.pathname, location.search, setLastDrilldownUrl]);

  // ── Parse URL params ──
  const dataTableId = searchParams.get('dataTable');
  const locationId = searchParams.get('locationId');
  const locationName = searchParams.get('locationName');
  const meta = dataTableId ? OPS_DRILLDOWN_META[dataTableId] ?? null : null;

  const dateRange = useMemo(() => {
    const dr = searchParams.get('dateRange');
    if (dr && VALID_DATE_RANGES.has(dr)) return dr as DateRangeType;
    return 'lastMonthCal' as DateRangeType;
  }, [searchParams]);

  const customDateRange = useMemo(() => {
    const cs = searchParams.get('customStart');
    const ce = searchParams.get('customEnd');
    if (cs && ce && dateRange === 'custom') {
      const start = new Date(cs);
      const end = new Date(ce);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()))
        return { start, end };
    }
    return undefined;
  }, [searchParams, dateRange]);

  const regionIds = useMemo(() => {
    const rid = searchParams.get('regionId');
    return rid ? [rid] : undefined;
  }, [searchParams]);

  const locationIds = useMemo(() => {
    return locationId ? [locationId] : undefined;
  }, [locationId]);

  const activeStatuses = useMemo(() => {
    const raw = searchParams.get('activeStatuses');
    if (raw) return raw.split(',') as DonorStatus[];
    // Use status filter map for specific table types
    if (dataTableId && STATUS_FILTER_MAP[dataTableId]) {
      return STATUS_FILTER_MAP[dataTableId];
    }
    return opsSettings.activeDonorStatuses?.length
      ? opsSettings.activeDonorStatuses
      : undefined;
  }, [searchParams, dataTableId, opsSettings.activeDonorStatuses]);

  // Determine if this table type needs period-scoped data
  const needsPeriod = dataTableId ? PERIOD_TABLES.has(dataTableId) : false;

  // Determine if this table type filters by registration date (createdAt)
  const needsRegistration = dataTableId
    ? REGISTRATION_TABLES.has(dataTableId)
    : false;

  // Compute registrationRange from the date range bounds
  const registrationRange = useMemo(() => {
    if (!needsRegistration) return undefined;
    const bounds = getDateRangeBounds(dateRange, customDateRange);
    return {
      start: bounds.start.toISOString(),
      end: bounds.end.toISOString(),
    };
  }, [needsRegistration, dateRange, customDateRange]);

  // Overview metrics
  const isOverviewMetric = dataTableId?.startsWith('overview:') ?? false;
  const isWooSales = dataTableId === 'overview:sales';
  const isUnitsShipped = dataTableId === 'overview:unitsShipped';
  const isWastage = dataTableId === 'overview:wastage';
  const isTurnover = dataTableId === 'overview:turnover';
  const needsOverviewData =
    isWooSales || isUnitsShipped || isWastage || isTurnover;

  // Fetch overview stats for WooCommerce and units shipped drilldowns
  const overviewBounds = useMemo(
    () => getDateRangeBounds(dateRange, customDateRange),
    [dateRange, customDateRange]
  );
  const { data: overviewData, loading: overviewLoading } = useQuery(
    GET_OPS_OVERVIEW_STATS_QUERY,
    {
      variables: {
        input: {
          start: overviewBounds.start.toISOString(),
          end: overviewBounds.end.toISOString(),
          regionIds,
        },
      },
      skip: !needsOverviewData,
      fetchPolicy: 'cache-and-network',
    }
  );
  const wooOrders = useMemo(
    () => overviewData?.getOpsOverviewStats?.wooOrders ?? [],
    [overviewData]
  );

  const shippedUnits = useMemo(
    () => overviewData?.getOpsOverviewStats?.shippedUnitEntries ?? [],
    [overviewData]
  );

  const discardedUnits = useMemo(
    () => overviewData?.getOpsOverviewStats?.discardedUnitEntries ?? [],
    [overviewData]
  );

  const turnoverEntries = useMemo(
    () => overviewData?.getOpsOverviewStats?.turnoverEntries ?? [],
    [overviewData]
  );

  // WooCommerce summary breakdown by location
  const wooSummary = useMemo(() => {
    if (!isWooSales || !wooOrders.length) return null;
    const prbcIds = new Set(opsSettings.wcPrbcProductIds ?? [411, 463]);
    const ffpSglIds = new Set(opsSettings.wcFfpSglProductIds ?? [502, 8179]);
    const ffpDblIds = new Set(opsSettings.wcFfpDblProductIds ?? [503, 504]);

    interface ILocSummary {
      revenue: number;
      prbcCount: number;
      prbcRevenue: number;
      ffpSglCount: number;
      ffpSglRevenue: number;
      ffpDblCount: number;
      ffpDblRevenue: number;
      otherCount: number;
      otherRevenue: number;
    }
    const locMap = new Map<string, ILocSummary>();
    const empty = (): ILocSummary => ({
      revenue: 0,
      prbcCount: 0,
      prbcRevenue: 0,
      ffpSglCount: 0,
      ffpSglRevenue: 0,
      ffpDblCount: 0,
      ffpDblRevenue: 0,
      otherCount: 0,
      otherRevenue: 0,
    });

    for (const order of wooOrders) {
      const locName = (order as any).shipLocationName || 'Unknown';
      if (!locMap.has(locName)) locMap.set(locName, empty());
      const loc = locMap.get(locName)!;
      loc.revenue += order.total;

      for (const item of order.lineItems) {
        const qty = item.quantity;
        const itemRevenue = item.price * qty;
        if (prbcIds.has(item.productId)) {
          loc.prbcCount += qty;
          loc.prbcRevenue += itemRevenue;
        } else if (ffpSglIds.has(item.productId)) {
          loc.ffpSglCount += qty;
          loc.ffpSglRevenue += itemRevenue;
        } else if (ffpDblIds.has(item.productId)) {
          loc.ffpDblCount += qty;
          loc.ffpDblRevenue += itemRevenue;
        } else {
          loc.otherCount += qty;
          loc.otherRevenue += itemRevenue;
        }
      }
    }

    const locations = Array.from(locMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const totals = locations.reduce(
      (acc, l) => ({
        revenue: acc.revenue + l.revenue,
        prbcCount: acc.prbcCount + l.prbcCount,
        prbcRevenue: acc.prbcRevenue + l.prbcRevenue,
        ffpSglCount: acc.ffpSglCount + l.ffpSglCount,
        ffpSglRevenue: acc.ffpSglRevenue + l.ffpSglRevenue,
        ffpDblCount: acc.ffpDblCount + l.ffpDblCount,
        ffpDblRevenue: acc.ffpDblRevenue + l.ffpDblRevenue,
        otherCount: acc.otherCount + l.otherCount,
        otherRevenue: acc.otherRevenue + l.otherRevenue,
      }),
      empty()
    );

    return { locations, totals };
  }, [isWooSales, wooOrders, opsSettings]);

  // Retiring-in-days filter (retiringIn30 → 30, retiringIn60 → 60, retiringIn90 → 90)
  const retiringInDays = useMemo(() => {
    const match = dataTableId?.match(/^retiringIn(\d+)$/);
    return match ? parseInt(match[1], 10) : undefined;
  }, [dataTableId]);

  // Days in period (passed via URL for summary:units)
  const daysInPeriod = useMemo(() => {
    const d = searchParams.get('daysInPeriod');
    return d ? parseInt(d, 10) : 1;
  }, [searchParams]);

  // Location stats query (for summary:units view)
  const isUnitsView = dataTableId === 'summary:units';
  const locStatsBounds = useMemo(
    () => getDateRangeBounds(dateRange, customDateRange),
    [dateRange, customDateRange]
  );
  const { data: locStatsData, loading: locStatsLoading } = useQuery(
    GET_RECRUITER_LOCATION_STATS_QUERY,
    {
      variables: {
        input: {
          recruiterId: searchParams.get('recruiterId') || undefined,
          regionIds,
          activeDonorStatuses: opsSettings.activeDonorStatuses,
          activeLocationThresholdDays: 90,
          dateFrom: locStatsBounds.start.toISOString(),
          dateTo: locStatsBounds.end.toISOString(),
        },
      },
      skip: !isUnitsView,
    }
  );

  const isNewRegistration = dataTableId === 'newRegistration';

  const {
    donors: periodDonors,
    loading,
    periodRange,
  } = usePrbcDonationDonors({
    dateRange: needsPeriod ? dateRange : undefined,
    customDateRange: needsPeriod ? customDateRange : undefined,
    regionIds,
    locationIds,
    activeDonorStatuses: activeStatuses,
    registrationRange,
    retiringInDays,
    requireActiveHistory:
      dataTableId === 'appliedToActive' || dataTableId === 'activeToRetired'
        ? true
        : undefined,
    requireRetiredHistory: dataTableId === 'activeToRetired' ? true : undefined,
    requireApprovedHistory:
      dataTableId === 'pendingToApproved' ? true : undefined,
    requireArchivedHistory:
      dataTableId === 'pendingToArchived' ? true : undefined,
    enabled: !!dataTableId,
  });

  // For newRegistration: also load all-time donors (no registration range) so we
  // can guarantee at least 5 most-recent registrations when the period has 0.
  const { donors: allTimeDonors, loading: allTimeLoading } =
    usePrbcDonationDonors({
      locationIds,
      activeDonorStatuses: activeStatuses,
      enabled: isNewRegistration,
    });

  // Merge: if period returned results, show those (min 5 total by appending
  // all-time sorted by reg date); if period is empty, show last 5 all-time.
  const donors = useMemo(() => {
    if (!isNewRegistration) return periodDonors;
    const allTimeSorted = [...allTimeDonors].sort((a, b) => {
      const at = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
      const bt = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
      return bt - at;
    });
    if (periodDonors.length === 0) {
      return allTimeSorted.slice(0, 5);
    }
    // Period donors + top up to 5 from all-time (deduped)
    const periodIds = new Set(periodDonors.map((d) => d.donorId));
    const topUp = allTimeSorted
      .filter((d) => !periodIds.has(d.donorId))
      .slice(0, Math.max(0, 5 - periodDonors.length));
    return [...periodDonors, ...topUp];
  }, [isNewRegistration, periodDonors, allTimeDonors]);

  // ── Location Units (product-level) query ──
  const isLocationUnits = dataTableId === 'locationUnits';
  const locationUnitsBounds = useMemo(
    () =>
      isLocationUnits && dateRange
        ? getDateRangeBounds(dateRange, customDateRange)
        : null,
    [isLocationUnits, dateRange, customDateRange]
  );
  const [
    fetchLocationUnits,
    { data: locationUnitsData, loading: locationUnitsLoading },
  ] = useLazyQuery(GET_OPS_LOCATION_UNITS);

  useEffect(() => {
    if (!isLocationUnits) return;
    fetchLocationUnits({
      variables: {
        input: {
          locationIds: locationIds?.length ? locationIds : undefined,
          regionIds: regionIds?.length ? regionIds : undefined,
        },
      },
      fetchPolicy: 'network-only',
    });
  }, [isLocationUnits, locationIds, regionIds, fetchLocationUnits]);

  const locationUnitRows: Array<{
    productId: string;
    productShortId: number;
    unitType: string;
    productStatus: string;
    productCreatedAt: string;
    donorId: string;
    donorShortId: number;
    donorName: string;
    ownerFullName: string | null;
    ownerShortId: number | null;
    donorLocation: string | null;
    regionName: string | null;
    donorStatus: string | null;
    donationDate: string | null;
  }> = useMemo(
    () => locationUnitsData?.getOpsLocationUnits?.rows ?? [],
    [locationUnitsData]
  );

  // Group product rows by event date for event-based table
  const locationUnitEvents = useMemo(() => {
    if (!isLocationUnits || locationUnitRows.length === 0) return [];
    const eventMap = new Map<
      string,
      {
        eventDate: string;
        location: string;
        region: string;
        unitCount: number;
        donorIds: Set<string>;
        rows: typeof locationUnitRows;
      }
    >();
    for (const row of locationUnitRows) {
      const dateKey = row.donationDate
        ? format(new Date(row.donationDate), 'yyyy-MM-dd')
        : 'unknown';
      let event = eventMap.get(dateKey);
      if (!event) {
        event = {
          eventDate: dateKey,
          location: row.donorLocation || '—',
          region: row.regionName || '—',
          unitCount: 0,
          donorIds: new Set(),
          rows: [],
        };
        eventMap.set(dateKey, event);
      }
      event.unitCount++;
      event.donorIds.add(row.donorId);
      event.rows.push(row);
    }
    return Array.from(eventMap.values())
      .map((e) => ({
        eventDate: e.eventDate,
        location: e.location,
        region: e.region,
        unitCount: e.unitCount,
        donorCount: e.donorIds.size,
        rows: e.rows,
      }))
      .sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  }, [isLocationUnits, locationUnitRows]);

  const [selectedEvent, setSelectedEvent] = useState<
    (typeof locationUnitEvents)[number] | null
  >(null);

  const total = isLocationUnits ? locationUnitEvents.length : donors.length;

  const periodLabel = dateRange
    ? getDateRangeDisplayLabel(dateRange)
    : undefined;

  const columns = useMemo(
    () =>
      buildColumns(donors, periodLabel, dataTableId ?? undefined, isDarkMode),
    [donors, periodLabel, dataTableId, isDarkMode]
  );

  // ── CSV export ──
  const handleExport = useCallback(() => {
    // Location units: event-level export
    if (dataTableId === 'locationUnits') {
      const headers = [
        'Event Date',
        'Location',
        'Region',
        'Total Units',
        'Total Donors',
      ];
      const csvRows = locationUnitEvents.map((e) => [
        e.eventDate !== 'unknown'
          ? format(new Date(e.eventDate + 'T00:00:00'), 'MM/dd/yyyy')
          : '',
        e.location,
        e.region,
        String(e.unitCount),
        String(e.donorCount),
      ]);
      const csv = [headers, ...csvRows].map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `location-events-${locationName || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    const rows = filteredDonorsRef.current ?? donors;
    const isRetiringExport = dataTableId?.startsWith('retiringIn') ?? false;
    const isRetiredExport = dataTableId === 'retired';
    const isAppliedToActiveExport =
      dataTableId === 'appliedToActive' || dataTableId === 'activeToRetired';
    const headers = [
      'Donor UUID',
      'Donor Short ID',
      'Date Registered',
      ...(isAppliedToActiveExport ? ['Active Date', 'To Active Days'] : []),
      'Owner',
      'Donor',
      ...(isRetiredExport ? [] : ['Breed']),
      ...(isRetiringExport ? ['Birthday', 'Retires On'] : []),
      ...(isRetiredExport
        ? ['Birthday', 'Retired Date', 'Status', 'Retired Reason']
        : []),
      'Location',
      'Region',
      ...(!isAppliedToActiveExport && !isRetiredExport
        ? ['Last Donation']
        : []),
      ...(isRetiringExport ? ['TTM Success Rate', 'Time in Prog'] : []),
      ...(!isRetiredExport ? ['Status'] : []),
      'SGL/DBL',
      ...(!isRetiredExport && !isAppliedToActiveExport && !isRetiringExport
        ? ['Units']
        : []),
      ...(!isRetiringExport ? ['Total Donations'] : []),
      ...(!isAppliedToActiveExport ? ['Blood Type'] : []),
    ];
    const csvRows = rows.map((d) => {
      const dob = d.dateOfBirth ? new Date(d.dateOfBirth) : null;
      const retiresOn = dob
        ? new Date(dob.getFullYear() + 9, dob.getMonth(), dob.getDate())
        : null;
      const toActiveDays =
        d.registeredAt && d.activeDate
          ? Math.round(
              (new Date(d.activeDate).getTime() -
                new Date(d.registeredAt).getTime()) /
                86400000
            )
          : null;
      return [
        d.donorId,
        String(d.donorShortId),
        formatCsvDate(d.registeredAt),
        ...(isAppliedToActiveExport
          ? [
              formatCsvDate(d.activeDate),
              toActiveDays != null && toActiveDays >= 0
                ? String(toActiveDays)
                : '',
            ]
          : []),
        d.ownerFullName || '',
        d.donorName,
        ...(isRetiredExport ? [] : [d.breed || '']),
        ...(isRetiringExport
          ? [formatCsvDateOnly(dob), formatCsvDateOnly(retiresOn)]
          : []),
        ...(isRetiredExport
          ? [
              formatCsvDateOnly(dob),
              formatCsvDate(d.retiredDate),
              d.status,
              d.retiredReason || '',
            ]
          : []),
        d.ownerAssignedLocations || d.location || '',
        d.region || '',
        ...(!isAppliedToActiveExport && !isRetiredExport
          ? [formatCsvDate(d.lastDonationDate)]
          : []),
        ...(isRetiringExport
          ? (() => {
              const ttm = (() => {
                if (!d.registeredAt) return '';
                const days = Math.max(
                  Math.round(
                    (Date.now() - new Date(d.registeredAt).getTime()) / 86400000
                  ),
                  1
                );
                return ((d.periodUnits / days) * 365).toFixed(1);
              })();
              const tip = (() => {
                if (!d.registeredAt) return '';
                const start = new Date(d.registeredAt);
                const now = new Date();
                let years = now.getFullYear() - start.getFullYear();
                let months = now.getMonth() - start.getMonth();
                let days = now.getDate() - start.getDate();
                if (days < 0) {
                  months -= 1;
                  days += new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    0
                  ).getDate();
                }
                if (months < 0) {
                  years -= 1;
                  months += 12;
                }
                return `${years}y ${months}m ${days}d`;
              })();
              return [ttm, tip];
            })()
          : []),
        ...(!isRetiredExport ? [d.status] : []),
        d.sglDbl,
        ...(!isRetiredExport && !isAppliedToActiveExport && !isRetiringExport
          ? [String(d.periodUnits)]
          : []),
        ...(!isRetiringExport ? [String(d.totalDonations)] : []),
        ...(!isAppliedToActiveExport ? [d.bloodTypeDisplay] : []),
      ];
    });
    const csv = [headers, ...csvRows]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ops-drilldown-${dataTableId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [donors, dataTableId, locationUnitEvents, locationName]);

  // ── Load all location donors ──
  const handleLoadAllLocationDonors = useCallback(() => {
    if (!locationId) return;
    const url = buildOpsDrilldownUrl({
      dataTable: 'allLocationDonors',
      locationId,
      locationName: locationName || undefined,
    });
    navigate(url);
  }, [locationId, locationName, navigate]);

  // ── Back navigation ──
  const handleBack = useCallback(() => {
    navigate(
      isOverviewMetric
        ? Routes.DashboardsOperationsOverview
        : Routes.DashboardsOperationsRecruiter
    );
  }, [navigate, isOverviewMetric]);

  // ── Units view data ──
  const locEntries: Array<{
    locationId: string;
    locationName: string;
    regionName?: string;
    sglDonorCount: number;
    dblDonorCount: number;
    unitDonorCount: number;
    totalUnits: number;
    avgUnitsPerDay: number;
  }> = useMemo(() => {
    if (!isUnitsView) return [];
    const raw = locStatsData?.getRecruiterLocationStats?.entries ?? [];
    return raw
      .filter((e: any) => (e.totalUnits ?? 0) > 0)
      .map((e: any) => ({
        locationId: e.locationId,
        locationName: e.locationName,
        regionName: e.regionName,
        sglDonorCount: e.sglDonorCount ?? 0,
        dblDonorCount: e.dblDonorCount ?? 0,
        unitDonorCount: e.unitDonorCount ?? 0,
        totalUnits: e.totalUnits ?? 0,
        avgUnitsPerDay:
          daysInPeriod > 0 ? (e.totalUnits ?? 0) / daysInPeriod : 0,
      }))
      .sort((a: any, b: any) => b.totalUnits - a.totalUnits);
  }, [isUnitsView, locStatsData, daysInPeriod]);

  const locTotals = useMemo(
    () => ({
      sglDonorCount: locEntries.reduce((s, r) => s + r.sglDonorCount, 0),
      dblDonorCount: locEntries.reduce((s, r) => s + r.dblDonorCount, 0),
      unitDonorCount: locEntries.reduce((s, r) => s + r.unitDonorCount, 0),
      totalUnits: locEntries.reduce((s, r) => s + r.totalUnits, 0),
      avgUnitsPerDay: locEntries.reduce((s, r) => s + r.avgUnitsPerDay, 0),
    }),
    [locEntries]
  );

  const handleUnitsExport = useCallback(() => {
    const headers = [
      'Location',
      'Region',
      'SGL Donors',
      'DBL Donors',
      'Unit Donors',
      'Total Units',
      'Avg Units/Day',
    ];
    const rows = locEntries.map((r) => [
      r.locationName,
      r.regionName ?? '',
      String(r.sglDonorCount),
      String(r.dblDonorCount),
      String(r.unitDonorCount),
      String(r.totalUnits),
      r.avgUnitsPerDay.toFixed(2),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ops-units-by-location.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [locEntries]);

  // ── Empty state ──
  if (!dataTableId || !meta) {
    return (
      <div className={`${styles.placeholderTab} ${styles.animFadeUp}`}>
        <div className={styles.placeholderIcon}>
          <ZoomInOutlined />
        </div>
        <div className={styles.placeholderTitle}>Drilldown</div>
        <div className={styles.placeholderDescription}>
          Click a drilldown button from another tab to load detailed data here.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.animFadeUp}>
      {/* Header */}
      <div className={styles.drilldownHeader}>
        <div
          className={styles.drilldownHeaderIcon}
          style={{
            background: `${meta.iconColor}20`,
            color: meta.iconColor,
          }}
        >
          <TableOutlined />
        </div>
        <div className={styles.drilldownHeaderInfo}>
          <span className={styles.drilldownHeaderTitle}>{meta.title}</span>
          <span className={styles.drilldownHeaderDesc}>{meta.description}</span>
          <span
            className={styles.drilldownHeaderCount}
            style={{ color: meta.iconColor }}
          >
            {isOverviewMetric ? (
              isWooSales ? (
                overviewLoading ? (
                  <Spin size="small" />
                ) : (
                  `${wooOrders.length} orders · $${
                    overviewData?.getOpsOverviewStats?.wooCommerceSalesTotal?.toLocaleString() ??
                    '0'
                  }`
                )
              ) : isUnitsShipped ? (
                overviewLoading ? (
                  <Spin size="small" />
                ) : (
                  `${shippedUnits.length} units shipped`
                )
              ) : isWastage ? (
                overviewLoading ? (
                  <Spin size="small" />
                ) : (
                  `${discardedUnits.length} units discarded`
                )
              ) : isTurnover ? (
                overviewLoading ? (
                  <Spin size="small" />
                ) : (
                  `${turnoverEntries.length} rows`
                )
              ) : (
                'Drilldown data'
              )
            ) : isUnitsView ? (
              locStatsLoading ? (
                <Spin size="small" />
              ) : (
                `${
                  locEntries.length
                } locations · ${locTotals.totalUnits.toLocaleString()} units`
              )
            ) : loading || locationUnitsLoading ? (
              <Spin size="small" />
            ) : isLocationUnits ? (
              `${total.toLocaleString()} events (${
                locationUnitRows.length
              } units)`
            ) : (
              `${total.toLocaleString()} donors`
            )}
          </span>
        </div>
        {locationId && (
          <button
            type="button"
            className={styles.drilldownBackBtn}
            onClick={handleLoadAllLocationDonors}
          >
            <TeamOutlined /> Load All Location Donors
          </button>
        )}
        <button
          type="button"
          className={styles.drilldownBackBtn}
          onClick={handleBack}
        >
          <ArrowLeftOutlined />{' '}
          {isOverviewMetric ? 'Back to Overview' : 'Back to Recruiter'}
        </button>
      </div>

      {/* Toolbar */}
      <div className={styles.drilldownToolbar}>
        {locationName && (
          <span className={styles.drilldownLocationTag}>
            <EnvironmentOutlined /> {locationName}
          </span>
        )}
        {periodLabel && (
          <span className={styles.drilldownLocationTag}>{periodLabel}</span>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className={styles.drilldownExportBtn}
          onClick={
            isWooSales
              ? () => {
                  const dateSuffix = format(new Date(), 'yyyy-MM-dd');
                  const downloadCsvBlob = (
                    headers: string[],
                    rows: string[][],
                    filename: string
                  ) => {
                    const csv = [headers, ...rows]
                      .map((r) =>
                        r
                          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                          .join(',')
                      )
                      .join('\n');
                    const blob = new Blob([csv], {
                      type: 'text/csv',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  };

                  // CSV 1: Summary breakdown
                  if (wooSummary) {
                    const sumHeaders = [
                      'Location',
                      'Total Revenue',
                      'Total Units',
                      'pRBC Units',
                      'pRBC Revenue',
                      'FFP SGL Units',
                      'FFP SGL Revenue',
                      'FFP DBL Units',
                      'FFP DBL Revenue',
                      'Other Units',
                      'Other Revenue',
                    ];
                    const sumRows = wooSummary.locations.map((loc) => [
                      loc.name,
                      `$${loc.revenue.toFixed(2)}`,
                      String(
                        loc.prbcCount +
                          loc.ffpSglCount +
                          loc.ffpDblCount +
                          loc.otherCount
                      ),
                      String(loc.prbcCount),
                      `$${loc.prbcRevenue.toFixed(2)}`,
                      String(loc.ffpSglCount),
                      `$${loc.ffpSglRevenue.toFixed(2)}`,
                      String(loc.ffpDblCount),
                      `$${loc.ffpDblRevenue.toFixed(2)}`,
                      String(loc.otherCount),
                      `$${loc.otherRevenue.toFixed(2)}`,
                    ]);
                    const t = wooSummary.totals;
                    sumRows.push([
                      'TOTAL',
                      `$${t.revenue.toFixed(2)}`,
                      String(
                        t.prbcCount +
                          t.ffpSglCount +
                          t.ffpDblCount +
                          t.otherCount
                      ),
                      String(t.prbcCount),
                      `$${t.prbcRevenue.toFixed(2)}`,
                      String(t.ffpSglCount),
                      `$${t.ffpSglRevenue.toFixed(2)}`,
                      String(t.ffpDblCount),
                      `$${t.ffpDblRevenue.toFixed(2)}`,
                      String(t.otherCount),
                      `$${t.otherRevenue.toFixed(2)}`,
                    ]);
                    downloadCsvBlob(
                      sumHeaders,
                      sumRows,
                      `woo-sales-summary-${dateSuffix}.csv`
                    );
                  }

                  // CSV 2: Orders detail (slight delay so browser handles two downloads)
                  setTimeout(() => {
                    const ordHeaders = [
                      'Order #',
                      'Date',
                      'Status',
                      'Customer',
                      'Company',
                      'Ship Location',
                      'Class',
                      'Split',
                      'Items',
                      'Total',
                    ];
                    const ordRows = wooOrders.map((o: any) => [
                      String(o.orderNumber),
                      formatCsvDate(o.dateCreated),
                      o.status || '',
                      o.customerName || '',
                      o.company || '',
                      o.shipLocationName || '',
                      o.shippingClassSlug || '',
                      o.splitType || '',
                      String(o.lineItems?.length ?? 0),
                      `$${o.total.toFixed(2)}`,
                    ]);
                    downloadCsvBlob(
                      ordHeaders,
                      ordRows,
                      `woo-sales-orders-${dateSuffix}.csv`
                    );
                  }, 200);
                }
              : isWastage
              ? () => {
                  const prbcSet = new Set(
                    opsSettings.wcPrbcProductIds ?? [411, 463]
                  );
                  const ffpSglSet = new Set(
                    opsSettings.wcFfpSglProductIds ?? [502, 8179]
                  );
                  const ffpDblSet = new Set(
                    opsSettings.wcFfpDblProductIds ?? [503, 504]
                  );
                  const getType = (pid: number) => {
                    if (prbcSet.has(pid)) return 'pRBC';
                    if (ffpSglSet.has(pid)) return 'FFP SGL';
                    if (ffpDblSet.has(pid)) return 'FFP DBL';
                    return 'Other';
                  };
                  const headers = [
                    'Product',
                    'Discard Date',
                    'Reason',
                    'Notes',
                    'Type',
                    'Donation Date',
                    'Donor',
                    'Owner',
                    'Location',
                    'Region',
                  ];
                  const rows = discardedUnits.map((u: any) => [
                    String(u.productShortId),
                    formatCsvDate(u.createdAt),
                    u.discardReason || '',
                    u.discardNotes || '',
                    getType(u.woocommerceProductId),
                    formatCsvDate(u.donationDate),
                    u.donorName || '',
                    u.ownerName || '',
                    u.location || '',
                    u.regionName || '',
                  ]);
                  const csv = [headers, ...rows]
                    .map((r) =>
                      r
                        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                        .join(',')
                    )
                    .join('\n');
                  const blob = new Blob([csv], {
                    type: 'text/csv',
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `wastage-discards-${format(
                    new Date(),
                    'yyyy-MM-dd'
                  )}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              : isUnitsShipped
              ? () => {
                  const prbcSet = new Set(
                    opsSettings.wcPrbcProductIds ?? [411, 463]
                  );
                  const ffpSglSet = new Set(
                    opsSettings.wcFfpSglProductIds ?? [502, 8179]
                  );
                  const ffpDblSet = new Set(
                    opsSettings.wcFfpDblProductIds ?? [503, 504]
                  );
                  const getType = (pid: number) => {
                    if (prbcSet.has(pid)) return 'pRBC';
                    if (ffpSglSet.has(pid)) return 'FFP SGL';
                    if (ffpDblSet.has(pid)) return 'FFP DBL';
                    return 'Other';
                  };
                  const headers = [
                    'Product',
                    'Sold Date',
                    'Donation Date',
                    'Type',
                    'Order #',
                    'Donor',
                    'Owner',
                    'Location',
                    'Region',
                  ];
                  const rows = shippedUnits.map((u: any) => [
                    String(u.productShortId),
                    formatCsvDate(u.updatedAt),
                    formatCsvDate(u.donationDate),
                    getType(u.woocommerceProductId),
                    u.woocommerceOrderId ? String(u.woocommerceOrderId) : '',
                    u.donorName || '',
                    u.ownerName || '',
                    u.location || '',
                    u.regionName || '',
                  ]);
                  const csv = [headers, ...rows]
                    .map((r) =>
                      r
                        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                        .join(',')
                    )
                    .join('\n');
                  const blob = new Blob([csv], {
                    type: 'text/csv',
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `units-shipped-${format(
                    new Date(),
                    'yyyy-MM-dd'
                  )}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              : isTurnover
              ? () => {
                  const headers = [
                    'Unit Type',
                    'IN Qty (Produced)',
                    'OUT Qty (Sold)',
                    'Turnover %',
                    'Region',
                  ];
                  const rows = turnoverEntries.map((e: any) => [
                    e.unitType,
                    String(e.inQty),
                    String(e.outQty),
                    `${e.turnoverPct.toFixed(1)}%`,
                    e.regionName || '',
                  ]);
                  const totalIn = turnoverEntries.reduce(
                    (s: number, e: any) => s + e.inQty,
                    0
                  );
                  const totalOut = turnoverEntries.reduce(
                    (s: number, e: any) => s + e.outQty,
                    0
                  );
                  const totalPct = totalIn > 0 ? (totalOut / totalIn) * 100 : 0;
                  rows.push([
                    'Total',
                    String(totalIn),
                    String(totalOut),
                    `${totalPct.toFixed(1)}%`,
                    '',
                  ]);
                  const csv = [headers, ...rows]
                    .map((r) =>
                      r
                        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                        .join(',')
                    )
                    .join('\n');
                  const blob = new Blob([csv], {
                    type: 'text/csv',
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `inventory-turnover-${format(
                    new Date(),
                    'yyyy-MM-dd'
                  )}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              : isUnitsView
              ? handleUnitsExport
              : handleExport
          }
          disabled={
            isWooSales
              ? overviewLoading || wooOrders.length === 0
              : isWastage
              ? overviewLoading || discardedUnits.length === 0
              : isUnitsShipped
              ? overviewLoading || shippedUnits.length === 0
              : isTurnover
              ? overviewLoading || turnoverEntries.length === 0
              : isUnitsView
              ? locStatsLoading || locEntries.length === 0
              : loading || donors.length === 0
          }
        >
          <DownloadOutlined /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className={styles.drilldownTableWrap}>
        {isOverviewMetric ? (
          isTurnover ? (
            overviewLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : (
              <Table
                rowKey={(r: any) => `${r.unitType}-${r.regionName}`}
                dataSource={turnoverEntries}
                size="small"
                scroll={{ x: 600 }}
                pagination={false}
                columns={[
                  {
                    title: 'Unit Type',
                    dataIndex: 'unitType',
                    key: 'unitType',
                    width: 120,
                    filters: [
                      ...new Set(turnoverEntries.map((e: any) => e.unitType)),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.unitType === v,
                    render: (v: string) => (
                      <Tag
                        color={
                          v === 'pRBC'
                            ? 'purple'
                            : v === 'FFP SGL'
                            ? 'blue'
                            : v === 'FFP DBL'
                            ? 'cyan'
                            : 'default'
                        }
                      >
                        {v}
                      </Tag>
                    ),
                  },
                  {
                    title: 'IN Qty (Produced)',
                    dataIndex: 'inQty',
                    key: 'inQty',
                    width: 130,
                    align: 'right' as const,
                    sorter: (a: any, b: any) => a.inQty - b.inQty,
                    render: (v: number) => v.toLocaleString(),
                  },
                  {
                    title: 'OUT Qty (Sold)',
                    dataIndex: 'outQty',
                    key: 'outQty',
                    width: 130,
                    align: 'right' as const,
                    sorter: (a: any, b: any) => a.outQty - b.outQty,
                    render: (v: number) => v.toLocaleString(),
                  },
                  {
                    title: 'Turnover %',
                    dataIndex: 'turnoverPct',
                    key: 'turnoverPct',
                    width: 120,
                    align: 'right' as const,
                    defaultSortOrder: 'descend' as const,
                    sorter: (a: any, b: any) => a.turnoverPct - b.turnoverPct,
                    render: (v: number) => {
                      const color =
                        v >= 80
                          ? 'var(--status-up, #52c41a)'
                          : v >= 50
                          ? 'var(--status-warn, #faad14)'
                          : 'var(--status-down, #f5222d)';
                      return (
                        <span style={{ color, fontWeight: 700 }}>
                          {v.toFixed(1)}%
                        </span>
                      );
                    },
                  },
                  {
                    title: 'Region',
                    dataIndex: 'regionName',
                    key: 'regionName',
                    width: 120,
                    filters: [
                      ...new Set(
                        turnoverEntries
                          .map((e: any) => e.regionName)
                          .filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.regionName === v,
                    render: (v: string) => v || '—',
                  },
                ]}
                summary={() => {
                  const totalIn = turnoverEntries.reduce(
                    (s: number, e: any) => s + e.inQty,
                    0
                  );
                  const totalOut = turnoverEntries.reduce(
                    (s: number, e: any) => s + e.outQty,
                    0
                  );
                  const totalPct =
                    totalIn > 0
                      ? Math.round((totalOut / totalIn) * 1000) / 10
                      : 0;
                  const summaryStyle: React.CSSProperties = {
                    backgroundColor: isDarkMode
                      ? 'var(--bg-elevated, #1A1228)'
                      : '#fafafa',
                    color: isDarkMode ? 'var(--text-primary, #fff)' : '#2f3a45',
                  };
                  return (
                    <Table.Summary.Row style={summaryStyle}>
                      <Table.Summary.Cell index={0}>
                        <strong style={{ color: summaryStyle.color }}>
                          Total
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong style={{ color: summaryStyle.color }}>
                          {totalIn.toLocaleString()}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong style={{ color: summaryStyle.color }}>
                          {totalOut.toLocaleString()}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong style={{ color: summaryStyle.color }}>
                          {totalPct.toFixed(1)}%
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} />
                    </Table.Summary.Row>
                  );
                }}
                className={isDarkMode ? styles.drilldownTableDark : undefined}
              />
            )
          ) : isWastage ? (
            overviewLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : (
              <Table
                rowKey="productShortId"
                dataSource={discardedUnits}
                size="small"
                scroll={{ x: 1000 }}
                pagination={{
                  defaultPageSize: 50,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showSizeChanger: true,
                  showTotal: (t, range) =>
                    `${range[0]}-${range[1]} of ${t} discarded units`,
                }}
                columns={[
                  {
                    title: 'Product',
                    dataIndex: 'productShortId',
                    key: 'productShortId',
                    width: 80,
                    render: (v: number) => (
                      <Link
                        to={`/products/${v}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: isDarkMode ? '#4B9EFF' : '#1565c0',
                        }}
                      >
                        {v}
                      </Link>
                    ),
                    sorter: (a: any, b: any) =>
                      a.productShortId - b.productShortId,
                  },
                  {
                    title: 'Discard Date',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    width: 110,
                    defaultSortOrder: 'descend' as const,
                    sorter: (a: any, b: any) =>
                      new Date(a.createdAt).getTime() -
                      new Date(b.createdAt).getTime(),
                    render: (v: string) => format(new Date(v), 'MM/dd/yyyy'),
                  },
                  {
                    title: 'Reason',
                    dataIndex: 'discardReason',
                    key: 'discardReason',
                    width: 140,
                    filters: [
                      ...new Set(
                        discardedUnits
                          .map((u: any) => u.discardReason)
                          .filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.discardReason === v,
                    render: (v: string) => (
                      <Tag
                        color={
                          v === 'Timeout'
                            ? 'orange'
                            : v === 'Hemolyzed'
                            ? 'red'
                            : v === 'Clots'
                            ? 'volcano'
                            : v === 'LowVolume'
                            ? 'gold'
                            : v === 'Lipemic'
                            ? 'lime'
                            : v === 'Expired'
                            ? 'purple'
                            : 'default'
                        }
                      >
                        {v}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Notes',
                    dataIndex: 'discardNotes',
                    key: 'discardNotes',
                    width: 180,
                    ellipsis: { showTitle: false },
                    render: (v: string) =>
                      v ? (
                        <Tooltip title={v} placement="topLeft">
                          <span style={{ cursor: 'help' }}>{v}</span>
                        </Tooltip>
                      ) : (
                        '—'
                      ),
                  },
                  {
                    title: 'Type',
                    key: 'productType',
                    width: 90,
                    render: (_: unknown, record: any) => {
                      const prbcSet = new Set(
                        opsSettings.wcPrbcProductIds ?? [411, 463]
                      );
                      const ffpSglSet = new Set(
                        opsSettings.wcFfpSglProductIds ?? [502, 8179]
                      );
                      const ffpDblSet = new Set(
                        opsSettings.wcFfpDblProductIds ?? [503, 504]
                      );
                      const pid = record.woocommerceProductId;
                      if (prbcSet.has(pid))
                        return <Tag color="purple">pRBC</Tag>;
                      if (ffpSglSet.has(pid))
                        return <Tag color="blue">FFP SGL</Tag>;
                      if (ffpDblSet.has(pid))
                        return <Tag color="cyan">FFP DBL</Tag>;
                      return <Tag>Other</Tag>;
                    },
                    filters: [
                      { text: 'pRBC', value: 'prbc' },
                      { text: 'FFP SGL', value: 'ffpSgl' },
                      { text: 'FFP DBL', value: 'ffpDbl' },
                      { text: 'Other', value: 'other' },
                    ],
                    onFilter: (v: any, record: any) => {
                      const prbcSet = new Set(
                        opsSettings.wcPrbcProductIds ?? [411, 463]
                      );
                      const ffpSglSet = new Set(
                        opsSettings.wcFfpSglProductIds ?? [502, 8179]
                      );
                      const ffpDblSet = new Set(
                        opsSettings.wcFfpDblProductIds ?? [503, 504]
                      );
                      const pid = record.woocommerceProductId;
                      if (v === 'prbc') return prbcSet.has(pid);
                      if (v === 'ffpSgl') return ffpSglSet.has(pid);
                      if (v === 'ffpDbl') return ffpDblSet.has(pid);
                      return (
                        !prbcSet.has(pid) &&
                        !ffpSglSet.has(pid) &&
                        !ffpDblSet.has(pid)
                      );
                    },
                  },
                  {
                    title: 'Donation Date',
                    dataIndex: 'donationDate',
                    key: 'donationDate',
                    width: 110,
                    render: (v: string | null) =>
                      v ? format(new Date(v), 'MM/dd/yyyy') : '—',
                  },
                  {
                    title: 'Donor',
                    dataIndex: 'donorName',
                    key: 'donorName',
                    width: 120,
                    sorter: (a: any, b: any) =>
                      a.donorName.localeCompare(b.donorName),
                  },
                  {
                    title: 'Owner',
                    dataIndex: 'ownerName',
                    key: 'ownerName',
                    width: 140,
                    render: (v: string) => v || '—',
                  },
                  {
                    title: 'Location',
                    dataIndex: 'location',
                    key: 'location',
                    width: 120,
                    render: (v: string) => v || '—',
                    filters: [
                      ...new Set(
                        discardedUnits
                          .map((u: any) => u.location)
                          .filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.location === v,
                  },
                  {
                    title: 'Region',
                    dataIndex: 'regionName',
                    key: 'regionName',
                    width: 100,
                    render: (v: string) => v || '—',
                    filters: [
                      ...new Set(
                        discardedUnits
                          .map((u: any) => u.regionName)
                          .filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.regionName === v,
                  },
                ]}
                className={isDarkMode ? styles.drilldownTableDark : undefined}
              />
            )
          ) : isUnitsShipped ? (
            overviewLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : (
              <Table
                rowKey="productShortId"
                dataSource={shippedUnits}
                size="small"
                scroll={{ x: 1000 }}
                pagination={{
                  defaultPageSize: 50,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showSizeChanger: true,
                  showTotal: (t, range) =>
                    `${range[0]}-${range[1]} of ${t} units`,
                }}
                columns={[
                  {
                    title: 'Product',
                    dataIndex: 'productShortId',
                    key: 'productShortId',
                    width: 80,
                    render: (v: number) => (
                      <Link
                        to={`/products/${v}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: isDarkMode ? '#4B9EFF' : '#1565c0',
                        }}
                      >
                        {v}
                      </Link>
                    ),
                    sorter: (a: any, b: any) =>
                      a.productShortId - b.productShortId,
                  },
                  {
                    title: 'Sold Date',
                    dataIndex: 'updatedAt',
                    key: 'updatedAt',
                    width: 110,
                    defaultSortOrder: 'descend' as const,
                    sorter: (a: any, b: any) =>
                      new Date(a.updatedAt).getTime() -
                      new Date(b.updatedAt).getTime(),
                    render: (v: string) => format(new Date(v), 'MM/dd/yyyy'),
                  },
                  {
                    title: 'Donation Date',
                    dataIndex: 'donationDate',
                    key: 'donationDate',
                    width: 110,
                    render: (v: string | null) =>
                      v ? format(new Date(v), 'MM/dd/yyyy') : '—',
                    sorter: (a: any, b: any) =>
                      new Date(a.donationDate || 0).getTime() -
                      new Date(b.donationDate || 0).getTime(),
                  },
                  {
                    title: 'Type',
                    key: 'productType',
                    width: 90,
                    render: (_: unknown, record: any) => {
                      const prbcSet = new Set(
                        opsSettings.wcPrbcProductIds ?? [411, 463]
                      );
                      const ffpSglSet = new Set(
                        opsSettings.wcFfpSglProductIds ?? [502, 8179]
                      );
                      const ffpDblSet = new Set(
                        opsSettings.wcFfpDblProductIds ?? [503, 504]
                      );
                      const pid = record.woocommerceProductId;
                      if (prbcSet.has(pid))
                        return <Tag color="purple">pRBC</Tag>;
                      if (ffpSglSet.has(pid))
                        return <Tag color="blue">FFP SGL</Tag>;
                      if (ffpDblSet.has(pid))
                        return <Tag color="cyan">FFP DBL</Tag>;
                      return <Tag>Other</Tag>;
                    },
                    filters: [
                      { text: 'pRBC', value: 'prbc' },
                      { text: 'FFP SGL', value: 'ffpSgl' },
                      { text: 'FFP DBL', value: 'ffpDbl' },
                      { text: 'Other', value: 'other' },
                    ],
                    onFilter: (v: any, record: any) => {
                      const prbcSet = new Set(
                        opsSettings.wcPrbcProductIds ?? [411, 463]
                      );
                      const ffpSglSet = new Set(
                        opsSettings.wcFfpSglProductIds ?? [502, 8179]
                      );
                      const ffpDblSet = new Set(
                        opsSettings.wcFfpDblProductIds ?? [503, 504]
                      );
                      const pid = record.woocommerceProductId;
                      if (v === 'prbc') return prbcSet.has(pid);
                      if (v === 'ffpSgl') return ffpSglSet.has(pid);
                      if (v === 'ffpDbl') return ffpDblSet.has(pid);
                      return (
                        !prbcSet.has(pid) &&
                        !ffpSglSet.has(pid) &&
                        !ffpDblSet.has(pid)
                      );
                    },
                  },
                  {
                    title: 'Order #',
                    dataIndex: 'woocommerceOrderId',
                    key: 'woocommerceOrderId',
                    width: 80,
                    render: (v: number | null) =>
                      v ? (
                        <a
                          href={`https://www.navbb.com/wp-admin/post.php?post=${v}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: isDarkMode ? '#4B9EFF' : '#1565c0',
                          }}
                        >
                          {v}
                        </a>
                      ) : (
                        '—'
                      ),
                    sorter: (a: any, b: any) =>
                      (a.woocommerceOrderId ?? 0) - (b.woocommerceOrderId ?? 0),
                  },
                  {
                    title: 'Donor',
                    dataIndex: 'donorName',
                    key: 'donorName',
                    width: 120,
                    sorter: (a: any, b: any) =>
                      a.donorName.localeCompare(b.donorName),
                  },
                  {
                    title: 'Owner',
                    dataIndex: 'ownerName',
                    key: 'ownerName',
                    width: 140,
                    render: (v: string) => v || '—',
                    sorter: (a: any, b: any) =>
                      (a.ownerName || '').localeCompare(b.ownerName || ''),
                  },
                  {
                    title: 'Location',
                    dataIndex: 'location',
                    key: 'location',
                    width: 120,
                    render: (v: string) => v || '—',
                    filters: [
                      ...new Set(
                        shippedUnits.map((u: any) => u.location).filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.location === v,
                  },
                  {
                    title: 'Region',
                    dataIndex: 'regionName',
                    key: 'regionName',
                    width: 100,
                    render: (v: string) => v || '—',
                    filters: [
                      ...new Set(
                        shippedUnits
                          .map((u: any) => u.regionName)
                          .filter(Boolean)
                      ),
                    ].map((s) => ({
                      text: String(s),
                      value: String(s),
                    })),
                    onFilter: (v: any, r: any) => r.regionName === v,
                  },
                ]}
                className={isDarkMode ? styles.drilldownTableDark : undefined}
              />
            )
          ) : isWooSales ? (
            overviewLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : (
              <>
                {/* WooCommerce Summary Breakdown */}
                {wooSummary && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 16,
                      borderRadius: 8,
                      background: isDarkMode
                        ? 'var(--bg-elevated, #1A1228)'
                        : '#fafafa',
                      border: `1px solid ${
                        isDarkMode
                          ? 'var(--border-subtle, rgba(255,255,255,0.08))'
                          : '#e8e8e8'
                      }`,
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: 12,
                      color: isDarkMode
                        ? 'var(--text-secondary, #F0EBF8)'
                        : '#2f3a45',
                    }}
                  >
                    {wooSummary.locations.map((loc, i) => (
                      <div
                        key={loc.name}
                        style={{
                          marginBottom:
                            i < wooSummary.locations.length - 1 ? 12 : 0,
                          paddingBottom:
                            i < wooSummary.locations.length - 1 ? 12 : 0,
                          borderBottom:
                            i < wooSummary.locations.length - 1
                              ? `1px solid ${
                                  isDarkMode
                                    ? 'rgba(255,255,255,0.06)'
                                    : '#e8e8e8'
                                }`
                              : 'none',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            marginBottom: 4,
                            color: isDarkMode ? '#fff' : '#0e4d8f',
                          }}
                        >
                          {loc.name}
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: 11,
                              marginLeft: 12,
                              opacity: 0.7,
                            }}
                          >
                            Total: $
                            {loc.revenue.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            |{' '}
                            {loc.prbcCount +
                              loc.ffpSglCount +
                              loc.ffpDblCount +
                              loc.otherCount}{' '}
                            units
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '2px 24px',
                            paddingLeft: 12,
                            opacity: 0.8,
                          }}
                        >
                          <span>
                            pRBC: $
                            {loc.prbcRevenue.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            | {loc.prbcCount} units
                          </span>
                          <span>
                            FFP SGL: $
                            {loc.ffpSglRevenue.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            | {loc.ffpSglCount} units
                          </span>
                          <span>
                            FFP DBL: $
                            {loc.ffpDblRevenue.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            | {loc.ffpDblCount} units
                          </span>
                          {loc.otherCount > 0 && (
                            <span>
                              Other: $
                              {loc.otherRevenue.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              | {loc.otherCount} units
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* Org Totals */}
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: `2px solid ${
                          isDarkMode ? 'rgba(255,255,255,0.15)' : '#ccc'
                        }`,
                        fontWeight: 700,
                        fontSize: 13,
                        color: isDarkMode ? '#fff' : '#0e4d8f',
                      }}
                    >
                      <div style={{ marginBottom: 4 }}>
                        Org Totals: $
                        {wooSummary.totals.revenue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        |{' '}
                        {wooSummary.totals.prbcCount +
                          wooSummary.totals.ffpSglCount +
                          wooSummary.totals.ffpDblCount +
                          wooSummary.totals.otherCount}{' '}
                        units
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: '2px 24px',
                          paddingLeft: 12,
                          fontWeight: 600,
                          fontSize: 12,
                          opacity: 0.85,
                        }}
                      >
                        <span>pRBC: {wooSummary.totals.prbcCount} units</span>
                        <span>
                          FFP SGL: {wooSummary.totals.ffpSglCount} units
                        </span>
                        <span>
                          FFP DBL: {wooSummary.totals.ffpDblCount} units
                        </span>
                        {wooSummary.totals.otherCount > 0 && (
                          <span>
                            Other: {wooSummary.totals.otherCount} units
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <Table
                  rowKey="orderId"
                  dataSource={wooOrders}
                  size="small"
                  scroll={{ x: 1200 }}
                  pagination={{
                    defaultPageSize: 50,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    showTotal: (t, range) =>
                      `${range[0]}-${range[1]} of ${t} orders`,
                  }}
                  columns={[
                    {
                      title: 'Order #',
                      dataIndex: 'orderNumber',
                      key: 'orderNumber',
                      width: 80,
                      sorter: (a: any, b: any) => a.orderNumber - b.orderNumber,
                      render: (v: number) => (
                        <a
                          href={`https://www.navbb.com/wp-admin/post.php?post=${v}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: isDarkMode ? '#4B9EFF' : '#1565c0' }}
                        >
                          {v}
                        </a>
                      ),
                    },
                    {
                      title: 'Date',
                      dataIndex: 'dateCreated',
                      key: 'dateCreated',
                      width: 120,
                      defaultSortOrder: 'descend' as const,
                      sorter: (a: any, b: any) =>
                        new Date(a.dateCreated).getTime() -
                        new Date(b.dateCreated).getTime(),
                      render: (v: string) => format(new Date(v), 'MM/dd/yyyy'),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 100,
                      filters: [
                        ...new Set(wooOrders.map((o: any) => o.status)),
                      ].map((s) => ({ text: String(s), value: String(s) })),
                      onFilter: (v: any, r: any) => r.status === v,
                      render: (v: string) => (
                        <Tag
                          color={
                            v === 'completed'
                              ? 'green'
                              : v === 'completed-2'
                              ? 'cyan'
                              : v === 'delayed'
                              ? 'orange'
                              : 'default'
                          }
                        >
                          {v}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Customer',
                      dataIndex: 'customerName',
                      key: 'customerName',
                      width: 150,
                      sorter: (a: any, b: any) =>
                        a.customerName.localeCompare(b.customerName),
                    },
                    {
                      title: 'Company',
                      dataIndex: 'company',
                      key: 'company',
                      width: 180,
                      render: (v: string) => v || '—',
                      sorter: (a: any, b: any) =>
                        (a.company || '').localeCompare(b.company || ''),
                    },
                    {
                      title: 'Ship Location',
                      dataIndex: 'shipLocationName',
                      key: 'shipLocationName',
                      width: 120,
                      render: (v: string) => v || '—',
                      filters: [
                        ...new Set(
                          wooOrders
                            .map((o: any) => o.shipLocationName)
                            .filter(Boolean)
                        ),
                      ].map((s) => ({ text: String(s), value: String(s) })),
                      onFilter: (v: any, r: any) => r.shipLocationName === v,
                    },
                    {
                      title: 'Class',
                      dataIndex: 'shippingClassSlug',
                      key: 'shippingClassSlug',
                      width: 80,
                      render: (v: string) =>
                        v ? (
                          <Tag
                            color={
                              v === 'prbc'
                                ? 'purple'
                                : v === 'ffp'
                                ? 'blue'
                                : 'default'
                            }
                          >
                            {v.toUpperCase()}
                          </Tag>
                        ) : (
                          '—'
                        ),
                      filters: [
                        ...new Set(
                          wooOrders
                            .map((o: any) => o.shippingClassSlug)
                            .filter(Boolean)
                        ),
                      ].map((s) => ({
                        text: String(s).toUpperCase(),
                        value: String(s),
                      })),
                      onFilter: (v: any, r: any) => r.shippingClassSlug === v,
                    },
                    {
                      title: 'Split',
                      dataIndex: 'splitType',
                      key: 'splitType',
                      width: 60,
                      render: (v: string) => v || '—',
                    },
                    {
                      title: 'Items',
                      dataIndex: 'lineItems',
                      key: 'itemCount',
                      width: 60,
                      align: 'center' as const,
                      render: (items: any[]) => items?.length ?? 0,
                      sorter: (a: any, b: any) =>
                        (a.lineItems?.length ?? 0) - (b.lineItems?.length ?? 0),
                    },
                    {
                      title: 'Total',
                      dataIndex: 'total',
                      key: 'total',
                      width: 100,
                      align: 'right' as const,
                      sorter: (a: any, b: any) => a.total - b.total,
                      render: (v: number) =>
                        `$${v.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                    },
                  ]}
                  expandable={{
                    expandedRowRender: (record: any) => (
                      <div style={{ padding: '4px 0' }}>
                        {record.lineItems?.map((item: any) => (
                          <div
                            key={item.id}
                            style={{
                              display: 'flex',
                              gap: 16,
                              padding: '2px 0',
                              fontSize: 12,
                              color: isDarkMode ? '#F0EBF8' : '#2f3a45',
                            }}
                          >
                            <span style={{ flex: 1 }}>{item.name}</span>
                            <span style={{ width: 80 }}>
                              SKU: {item.sku || '—'}
                            </span>
                            <span style={{ width: 40 }}>×{item.quantity}</span>
                            <span
                              style={{
                                width: 80,
                                textAlign: 'right',
                              }}
                            >
                              $
                              {item.price.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ),
                  }}
                  className={isDarkMode ? styles.drilldownTableDark : undefined}
                />
              </>
            )
          ) : (
            <div
              style={{
                padding: '48px 24px',
                textAlign: 'center',
                color: isDarkMode ? '#9B8DB8' : '#6e7a8a',
              }}
            >
              <TableOutlined
                style={{
                  fontSize: 32,
                  marginBottom: 12,
                  opacity: 0.5,
                }}
              />
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {meta.title} — Drilldown
              </div>
              <div style={{ fontSize: 12 }}>
                Detailed drilldown table coming soon.
              </div>
            </div>
          )
        ) : isUnitsView ? (
          <table className={styles.unitsLocTable}>
            <thead>
              <tr>
                <th>Location</th>
                <th>Region</th>
                <th className={styles.unitsLocNum}>SGL Donors</th>
                <th className={styles.unitsLocNum}>DBL Donors</th>
                <th className={styles.unitsLocNum}>Unit Donors</th>
                <th className={styles.unitsLocNum}>Total Units</th>
                <th className={styles.unitsLocNum}>Avg Units/Day</th>
              </tr>
            </thead>
            <tbody>
              {locStatsLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: 'center', padding: '24px' }}
                  >
                    <Spin />
                  </td>
                </tr>
              ) : (
                locEntries.map((row) => (
                  <tr key={row.locationId}>
                    <td>{row.locationName}</td>
                    <td>{row.regionName ?? '—'}</td>
                    <td className={styles.unitsLocNum}>{row.sglDonorCount}</td>
                    <td className={styles.unitsLocNum}>{row.dblDonorCount}</td>
                    <td className={styles.unitsLocNum}>{row.unitDonorCount}</td>
                    <td className={styles.unitsLocNum}>{row.totalUnits}</td>
                    <td className={styles.unitsLocNum}>
                      {row.avgUnitsPerDay.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!locStatsLoading && locEntries.length > 0 && (
              <tfoot>
                <tr className={styles.unitsLocTotalsRow}>
                  <td colSpan={2}>Totals</td>
                  <td className={styles.unitsLocNum}>
                    {locTotals.sglDonorCount}
                  </td>
                  <td className={styles.unitsLocNum}>
                    {locTotals.dblDonorCount}
                  </td>
                  <td className={styles.unitsLocNum}>
                    {locTotals.unitDonorCount}
                  </td>
                  <td className={styles.unitsLocNum}>{locTotals.totalUnits}</td>
                  <td className={styles.unitsLocNum}>
                    {locTotals.avgUnitsPerDay.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : isLocationUnits ? (
          <>
            <Table
              dataSource={locationUnitEvents}
              columns={[
                {
                  title: 'Event Date',
                  dataIndex: 'eventDate',
                  key: 'eventDate',
                  width: 130,
                  render: (val: string) =>
                    val !== 'unknown'
                      ? format(new Date(val + 'T00:00:00'), 'MM/dd/yyyy')
                      : '—',
                  sorter: (a: any, b: any) =>
                    a.eventDate.localeCompare(b.eventDate),
                  defaultSortOrder: 'descend' as const,
                },
                {
                  title: 'Location',
                  dataIndex: 'location',
                  key: 'location',
                  width: 200,
                },
                {
                  title: 'Region',
                  dataIndex: 'region',
                  key: 'region',
                  width: 140,
                },
                {
                  title: 'Total Units',
                  dataIndex: 'unitCount',
                  key: 'unitCount',
                  width: 110,
                  sorter: (a: any, b: any) => a.unitCount - b.unitCount,
                },
                {
                  title: 'Total Donors',
                  dataIndex: 'donorCount',
                  key: 'donorCount',
                  width: 110,
                  sorter: (a: any, b: any) => a.donorCount - b.donorCount,
                },
              ]}
              rowKey="eventDate"
              loading={locationUnitsLoading}
              size="small"
              scroll={{ x: 700 }}
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ['25', '50', '100', '200'],
              }}
              onRow={(record) => ({
                onClick: () => setSelectedEvent(record),
                style: { cursor: 'pointer' },
              })}
            />
            <Modal
              visible={!!selectedEvent}
              title={`Units — ${
                selectedEvent
                  ? format(
                      new Date(selectedEvent.eventDate + 'T00:00:00'),
                      'MM/dd/yyyy'
                    )
                  : ''
              }`}
              onCancel={() => setSelectedEvent(null)}
              footer={null}
              width={800}
              destroyOnClose
              wrapClassName={
                isDarkMode ? styles.opsMetricModal : styles.opsMetricModalLight
              }
              bodyStyle={{
                background: isDarkMode ? '#0C0612' : '#FFFFFF',
                padding: '16px 20px',
              }}
            >
              {selectedEvent && (
                <Table
                  dataSource={selectedEvent.rows}
                  columns={[
                    {
                      title: 'Product ID',
                      dataIndex: 'productShortId',
                      key: 'productShortId',
                      width: 90,
                      render: (val: number) => (
                        <Link
                          to={`${Routes.Products}/${val}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {val}
                        </Link>
                      ),
                    },
                    {
                      title: 'Unit Type',
                      dataIndex: 'unitType',
                      key: 'unitType',
                      width: 80,
                    },
                    {
                      title: 'Donor',
                      dataIndex: 'donorName',
                      key: 'donorName',
                      width: 140,
                      render: (val: string, record: any) => (
                        <Link
                          to={`${Routes.Donors}/${record.donorShortId}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {val}
                        </Link>
                      ),
                    },
                    {
                      title: 'Donor ID',
                      dataIndex: 'donorShortId',
                      key: 'donorShortId',
                      width: 80,
                    },
                    {
                      title: 'Owner',
                      dataIndex: 'ownerFullName',
                      key: 'ownerFullName',
                      width: 140,
                      render: (val: string, record: any) =>
                        record.ownerShortId ? (
                          <Link
                            to={`${Routes.Owners}/${record.ownerShortId}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {val || '—'}
                          </Link>
                        ) : (
                          val || '—'
                        ),
                    },
                    {
                      title: 'Donor Status',
                      dataIndex: 'donorStatus',
                      key: 'donorStatus',
                      width: 100,
                    },
                  ]}
                  rowKey="productId"
                  size="small"
                  scroll={{ x: 600 }}
                  pagination={false}
                />
              )}
            </Modal>
          </>
        ) : (
          <Table<IDonorRecord>
            dataSource={donors}
            columns={columns}
            rowKey="donorId"
            loading={loading || (isNewRegistration && allTimeLoading)}
            size="small"
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
            }}
            onChange={(_pagination, _filters, _sorter, extra) => {
              filteredDonorsRef.current = extra.currentDataSource;
            }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedDonor(record);
                setDrilldownVisible(true);
              },
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </div>

      {/* Donation history modal */}
      <DonorDonationHistoryModal
        visible={drilldownVisible}
        donor={selectedDonor}
        onClose={() => {
          setDrilldownVisible(false);
          setSelectedDonor(null);
        }}
        periodStart={periodRange?.start}
        periodEnd={periodRange?.end}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default OpsDrilldownTab;
